/**
 * RoInput - 表单输入框 Web 组件
 * 支持：默认/选中/错误/禁用、浮动标签、前后图标、多种校验、表单关联 API
 * 用法：<ro-input placeholder="Email" float-label type="email" required></ro-input>
 */
class RoInput extends HTMLElement {
  static formAssociated = true;

  /** 需要同步到内部 input 的属性名 */
  static INPUT_ATTRS = ["pattern", "min", "max"];

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._internals = this.attachInternals();

    this._value = "";
    this._isFocused = false;
    this._isError = false;
    this._errorMessage = "";

    this._onInput = this._onInput.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onContainerClick = this._onContainerClick.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);

    this._render();
  }

  _render() {
    const inputId = `ro-input-${Math.random().toString(36).slice(2, 9)}`;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-flex;
          flex-direction: column;
          width: 100%;
        }
        .input-container {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          background: var(--bg-white-color, #fff);
          border: 1px solid var(--border-color, #e1e1e1);
          border-radius: 4px;
          transition: border-color 0.2s ease;
          cursor: text;
        }
        :host([focused]) .input-container { border-color: var(--main-color, #161616); }
        :host([error]) .input-container { border-color: var(--error-color, #d93840); }
        :host([disabled]) .input-container {
          background: rgba(225, 225, 225, 0.4);
          cursor: not-allowed;
        }
        .icon-slot {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          flex-shrink: 0;
          color: var(--second-color, #6c6d6e);
        }
        .icon-slot.empty { display: none; }
        .icon-slot::slotted(*) { width: 24px; height: 24px; display: block; }
        .input-wrapper {
          flex: 1;
          min-width: 0;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-height: 32px;
        }
        :host([float-label]) .input-container {
          height: 56px;
          box-sizing: border-box;
        }
        :host([float-label][has-value]) .input-wrapper,
        :host([float-label][focused]) .input-wrapper { justify-content: flex-start; }
        .label {
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          color: var(--second-color, #6c6d6e);
          font-family: 'Proxima Nova', sans-serif;
          font-size: 14px;
          line-height: 20px;
          pointer-events: none;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }
        :host([float-label][has-value]) .label,
        :host([float-label][focused]) .label {
          position: static;
          transform: none;
          font-size: 12px;
          line-height: 16px;
        }
        .input {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          color: var(--main-color, #161616);
          font-family: 'Proxima Nova', sans-serif;
          font-size: 16px;
          line-height: 1.5;
          padding: 0;
          margin: 0;
        }
        :host(:not([float-label])) .input::placeholder {
          color: var(--second-color, #6c6d6e);
          font-size: 14px;
          opacity: 1;
        }
        :host([float-label]) .input::placeholder { opacity: 0; }
        .input:disabled {
          cursor: not-allowed;
          color: var(--second-color, #6c6d6e);
        }
        .error-message {
          display: none;
          margin-top: 8px;
          padding: 0 16px;
          color: var(--error-color, #d93840);
          font-family: 'Proxima Nova', sans-serif;
          font-size: 12px;
          line-height: 16px;
        }
        :host([error]) .error-message { display: block; }
        @media (max-width: 1183px) {
          .input-container { padding: 12px; }
          .error-message { padding: 0 12px; }
        }
      </style>
      <div class="input-container">
        <slot name="prefix-icon" class="icon-slot"></slot>
        <div class="input-wrapper">
          <label class="label" for="${inputId}"></label>
          <input class="input" id="${inputId}" />
        </div>
        <slot name="suffix-icon" class="icon-slot"></slot>
      </div>
      <div class="error-message"></div>
    `;

    this._inputEl = this.shadowRoot.querySelector(".input");
    this._labelEl = this.shadowRoot.querySelector(".label");
    this._errorEl = this.shadowRoot.querySelector(".error-message");
    this._containerEl = this.shadowRoot.querySelector(".input-container");
    this._prefixSlot = this.shadowRoot.querySelector('slot[name="prefix-icon"]');
    this._suffixSlot = this.shadowRoot.querySelector('slot[name="suffix-icon"]');
  }

  connectedCallback() {
    this._inputEl.addEventListener("input", this._onInput);
    this._inputEl.addEventListener("focus", this._onFocus);
    this._inputEl.addEventListener("blur", this._onBlur);
    this._inputEl.addEventListener("change", this._onChange);
    this._containerEl.addEventListener("click", this._onContainerClick);
    this._prefixSlot.addEventListener("slotchange", this._onSlotChange);
    this._suffixSlot.addEventListener("slotchange", this._onSlotChange);

    this._updateInputAttributes();
    this._updateLabel();
    this._updateSlotVisibility(this._prefixSlot);
    this._updateSlotVisibility(this._suffixSlot);
  }

  disconnectedCallback() {
    this._inputEl.removeEventListener("input", this._onInput);
    this._inputEl.removeEventListener("focus", this._onFocus);
    this._inputEl.removeEventListener("blur", this._onBlur);
    this._inputEl.removeEventListener("change", this._onChange);
    this._containerEl.removeEventListener("click", this._onContainerClick);
    this._prefixSlot.removeEventListener("slotchange", this._onSlotChange);
    this._suffixSlot.removeEventListener("slotchange", this._onSlotChange);
  }

  static get observedAttributes() {
    return [
      "type", "placeholder", "value", "disabled", "required", "float-label",
      "pattern", "min-length", "max-length", "min", "max",
      "validate-fn", "validate-on-input", "error-message"
    ];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (["type", "disabled", "required", "pattern", "min", "max", "min-length", "max-length"].includes(name)) {
      this._updateInputAttributes();
      return;
    }
    if (["placeholder", "float-label"].includes(name)) {
      this._updateLabel();
      return;
    }
    if (name === "value") {
      this._updateValue(newValue);
      return;
    }
    if (name === "error-message" && this._isError) {
      this._errorEl.textContent = newValue || this._errorMessage;
    }
  }

  // ---------- 事件处理 ----------

  _onInput(e) {
    this._value = e.target.value;
    this.setAttribute("value", this._value);
    this._updateHasValueState();
    this._internals.setFormValue(this._value);
    this._dispatch("input", { value: this._value });
    if (this.hasAttribute("validate-on-input")) this.validate();
  }

  _onFocus() {
    this._isFocused = true;
    this.setAttribute("focused", "");
    this._dispatch("focus", { value: this._value });
  }

  _onBlur() {
    this._isFocused = false;
    this.removeAttribute("focused");
    if (!this.hasAttribute("validate-on-input")) this.validate();
    this._dispatch("blur", { value: this._value });
  }

  _onChange() {
    this._dispatch("change", { value: this._value });
  }

  /** 点击容器空白区域时聚焦输入框（避免点击图标也触发） */
  _onContainerClick(e) {
    if (this.hasAttribute("disabled")) return;
    const t = e.target;
    if (t.classList.contains("input-container") || t.classList.contains("input-wrapper") || t.classList.contains("label")) {
      this._inputEl.focus();
    }
  }

  /** slot 内容变化时更新图标占位显示/隐藏 */
  _onSlotChange(e) {
    this._updateSlotVisibility(e.target);
  }

  /** 派发自定义事件 */
  _dispatch(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  // ---------- 公共方法 ----------

  /** 执行校验，更新错误状态并派发 validate/error 事件 */
  validate() {
    if (this.hasAttribute("disabled")) return { valid: true, message: "" };

    const errorMessage =
      this._validateRequired() ??
      this._validateType() ??
      this._validatePattern() ??
      this._validateLength() ??
      this._validateRange() ??
      this._validateCustom() ??
      "";

    const valid = !errorMessage;
    this._setError(!valid, errorMessage);
    this._dispatch("validate", { valid, message: errorMessage, value: this._value });
    if (!valid) this._dispatch("error", { message: errorMessage, value: this._value });
    return { valid, message: errorMessage };
  }

  reset() {
    this._value = "";
    this._inputEl.value = "";
    this.setAttribute("value", "");
    this._setError(false, "");
    this._updateHasValueState();
    this._internals.setFormValue("");
  }

  clearError() {
    this._setError(false, "");
  }

  focus() {
    this._inputEl.focus();
  }

  blur() {
    this._inputEl.blur();
  }

  // ---------- 校验规则（按优先级，返回错误文案或 null） ----------

  _validateRequired() {
    if (!this.hasAttribute("required") || this._value.trim()) return null;
    return this._getErrorMessage("required") || "This field is required";
  }

  _validateType() {
    if (!this._value) return null;
    const type = this.getAttribute("type") || "text";
    if (type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this._value)) {
      return this._getErrorMessage("invalid_email") || "Please enter a valid email";
    }
    if (type === "tel" && !/^[0-9\s\-+()]+$/.test(this._value)) {
      return this._getErrorMessage("invalid_phone") || "Please enter a valid phone number";
    }
    if (type === "number" && isNaN(parseFloat(this._value))) {
      return "Please enter a valid number";
    }
    return null;
  }

  _validatePattern() {
    if (!this._value || !this.hasAttribute("pattern")) return null;
    const pattern = new RegExp(this.getAttribute("pattern"));
    return !pattern.test(this._value) ? (this._getErrorMessage("pattern") || "Invalid format") : null;
  }

  _validateLength() {
    if (!this._value) return null;
    const min = this.getAttribute("min-length");
    const max = this.getAttribute("max-length");
    if (min && this._value.length < parseInt(min, 10)) {
      return this._getErrorMessage("min_length", { min }) || `Minimum ${min} characters required`;
    }
    if (max && this._value.length > parseInt(max, 10)) {
      return this._getErrorMessage("max_length", { max }) || `Maximum ${max} characters allowed`;
    }
    return null;
  }

  _validateRange() {
    if (!this._value || this.getAttribute("type") !== "number") return null;
    const min = this.getAttribute("min");
    const max = this.getAttribute("max");
    const num = parseFloat(this._value);
    if (min != null && num < parseFloat(min)) return `Minimum value is ${min}`;
    if (max != null && num > parseFloat(max)) return `Maximum value is ${max}`;
    return null;
  }

  _validateCustom() {
    if (!this._value || !this.hasAttribute("validate-fn")) return null;
    const fn = window[this.getAttribute("validate-fn")];
    return typeof fn === "function" ? fn(this._value) : null;
  }

  // ---------- 辅助方法 ----------

  /** 根据 slot 是否有插槽内容切换 empty 类，控制占位是否显示 */
  _updateSlotVisibility(slotEl) {
    const hasContent = slotEl.assignedNodes({ flatten: true }).some(n => n.nodeType === Node.ELEMENT_NODE);
    slotEl.classList.toggle("empty", !hasContent);
  }

  /** 将宿主属性同步到内部 input 与 ARIA */
  _updateInputAttributes() {
    this._inputEl.type = this.getAttribute("type") || "text";
    this._inputEl.disabled = this.hasAttribute("disabled");
    this._inputEl.required = this.hasAttribute("required");
    RoInput.INPUT_ATTRS.forEach(attr => {
      const val = this.getAttribute(attr);
      if (val != null) this._inputEl[attr] = val;
    });
    const minLen = this.getAttribute("min-length");
    const maxLen = this.getAttribute("max-length");
    if (minLen != null) this._inputEl.minLength = parseInt(minLen, 10);
    if (maxLen != null) this._inputEl.maxLength = parseInt(maxLen, 10);
    this._updateAriaAttributes();
  }

  /** 根据 float-label 决定 placeholder 显示在 label 还是 input */
  _updateLabel() {
    const text = this.getAttribute("placeholder") || "";
    if (this.hasAttribute("float-label")) {
      this._labelEl.textContent = text;
      this._inputEl.placeholder = "";
    } else {
      this._labelEl.textContent = "";
      this._inputEl.placeholder = text;
    }
  }

  _updateValue(value) {
    this._value = value ?? "";
    this._inputEl.value = this._value;
    this._updateHasValueState();
    this._internals.setFormValue(this._value);
  }

  _updateHasValueState() {
    this.toggleAttribute("has-value", !!this._value);
  }

  _setError(isError, message) {
    this._isError = isError;
    this._errorMessage = message;
    this.toggleAttribute("error", isError);
    this._errorEl.textContent = isError ? (this.getAttribute("error-message") || message) : "";
    this._updateAriaAttributes();
  }

  _updateAriaAttributes() {
    this._inputEl.toggleAttribute("aria-invalid", this._isError);
    this._inputEl.toggleAttribute("aria-required", this.hasAttribute("required"));
    if (this._isError && this._errorMessage) {
      const id = this._errorEl.id || `error-${Math.random().toString(36).slice(2)}`;
      this._errorEl.id = id;
      this._inputEl.setAttribute("aria-describedby", id);
    } else {
      this._inputEl.removeAttribute("aria-describedby");
    }
  }

  /** 从 error-message 属性或 window.RoInputLocale 取文案，支持 {{ key }} 替换 */
  _getErrorMessage(key, params = {}) {
    const custom = this.getAttribute("error-message");
    if (custom) return custom;
    let msg = window.RoInputLocale?.[key] ?? null;
    if (msg && Object.keys(params).length) {
      Object.entries(params).forEach(([k, v]) => { msg = msg.replace(`{{ ${k} }}`, String(v)); });
    }
    return msg;
  }

  // ---------- 属性访问器 ----------

  get value() { return this._value; }
  set value(v) { this.setAttribute("value", v); }
  get type() { return this.getAttribute("type") || "text"; }
  set type(v) { this.setAttribute("type", v); }
  get disabled() { return this.hasAttribute("disabled"); }
  set disabled(v) { this.toggleAttribute("disabled", !!v); }
  get required() { return this.hasAttribute("required"); }
  set required(v) { this.toggleAttribute("required", !!v); }

  // ---------- 表单关联 API ----------

  formResetCallback() {
    this.value = "";
  }

  formDisabledCallback(disabled) {
    this.disabled = disabled;
  }

  formStateRestoreCallback(state, mode) {
    if (mode === "restore") this.value = state;
  }
}

customElements?.define("ro-input", RoInput);
